// frontend/src/components/modals/EnhancedAvatarModal.jsx
// Enhanced avatar modal with cropping, presets, and upload progress

import React, { useState, useRef } from 'react';
import { FiUpload, FiCrop, FiX, FiCheck, } from 'react-icons/fi';
import api from '../../services/api';
import { getDefaultAvatar, getUserAvatarUrl } from '../../utils/avatarUtils';

export default function EnhancedAvatarModal({ currentAvatar, onClose, onAvatarChange }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentAvatar);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cropMode, setCropMode] = useState(false);
  const fileInputRef = useRef(null);

  const avatarPresets = [
    '/avatar1.png',
    '/avatar2.png', 
    '/avatar3.png',
    '/avatar4.png',
    '/avatar5.png',
    '/avatar6.png'
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setCropMode(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Step 1: Get presigned URL from backend
      const presignResponse = await api.post('/avatar/presign', {
        fileType: selectedFile.type,
        fileName: selectedFile.name
      });

      if (!presignResponse.data.success) {
        throw new Error(presignResponse.data.message || 'Failed to get upload URL');
      }

      const { uploadUrl, publicUrl } = presignResponse.data;

      // Step 2: Upload directly to S3 using presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3');
      }

      setUploadProgress(100);

      // Step 3: Commit the URL to the database
      const commitResponse = await api.post('/avatar/commit', {
        url: publicUrl
      });

      if (!commitResponse.data.success) {
        throw new Error(commitResponse.data.message || 'Failed to save avatar');
      }

      // Success! Update the UI
      onAvatarChange(publicUrl);
      setPreviewUrl(publicUrl);
      
    } catch (error) {
      console.error('Upload failed:', error);
      alert(error.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePresetSelect = (presetUrl) => {
    setPreviewUrl(presetUrl);
    setSelectedFile(null);
    setCropMode(false);
    onAvatarChange(presetUrl);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div 
        className="modal-content avatar-modal" 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '600px',
          width: '90vw',
          maxHeight: '80vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <div className="modal-header">
          <h3>Change Profile Picture</h3>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {/* Current Preview */}
          <div className="avatar-preview-section" style={{ textAlign: 'center', maxWidth: '100%', overflow: 'hidden' }}>
            <div className="avatar-preview-large" style={{ 
              display: 'inline-block', 
              maxWidth: '200px', 
              maxHeight: '200px', 
              overflow: 'hidden',
              position: 'relative'
            }}>
              <div
                style={{
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  border: '4px solid #e2e8f0',
                  backgroundImage: `url(${previewUrl || getDefaultAvatar()})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#f5f5f5'
                }}
                aria-label="Avatar preview"
              />
              {cropMode && (
                <div className="crop-overlay">
                  <button className="crop-btn" onClick={() => setCropMode(false)}>
                    <FiCrop size={16} />
                    Crop
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Upload Section */}
          <div className="avatar-upload-section">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <div className="upload-buttons" style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button 
                className="btn btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <FiUpload size={16} />
                Upload New Photo
              </button>
              
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setPreviewUrl(getDefaultAvatar());
                  onAvatarChange(getDefaultAvatar());
                }}
                disabled={uploading}
                style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '4px' }}
              >
                <FiX size={16} />
                Remove Picture
              </button>
            </div>
            
            {selectedFile && (
              <div className="upload-actions">
                <button 
                  className="btn btn-success"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  <FiCheck size={16} />
                  {uploading ? 'Uploading...' : 'Save'}
                </button>
                
                {uploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <span>{uploadProgress}%</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Preset Avatars */}
          <div className="avatar-presets-section">
            <h4>Choose a Preset</h4>
            <div className="avatar-presets-grid">
              {avatarPresets.map((preset, index) => (
                <button
                  key={index}
                  className={`avatar-preset ${previewUrl === preset ? 'selected' : ''}`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  <img 
                    src={preset} 
                    alt={`Preset ${index + 1}`}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      maxWidth: '60px',
                      maxHeight: '60px'
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}